import { Channel, TChannelConfig } from "./contract"
import { foldToGAParams, gaEventName } from "../params"

declare global {
	/**
	 * The gtag command queue, defined inline in `index.html` before gtag.js
	 * loads (`dataLayer.push(arguments)`), under a name ad blockers do not
	 * match on.
	 */
	function analyticsManager(...args: unknown[]): void
}

export type TGAChannelConfig = TChannelConfig & {
	gaMeasurementId?: string
}

/**
 * Google Analytics 4 through gtag.js. The page sends nothing on its own —
 * `index.html` configures the tag with `send_page_view: false` — so every
 * page view and event here is explicit and bounded.
 */
const createGAChannel: Channel = function createGAChannel(
	config: TGAChannelConfig
) {
	const { gaMeasurementId, appVersion } = config
	if (!gaMeasurementId) {
		throw new Error("Missing measurement id for GA channel.")
	}
	const origin =
		typeof window !== "undefined" && window.location
			? window.location.origin
			: ""
	// A second `config` for the same id re-sends a page view unless told not
	// to; that is what inflated the counts before.
	const setUserId = (userId: string | number | undefined) => {
		analyticsManager("config", gaMeasurementId, {
			user_id: userId,
			send_page_view: false,
		})
	}
	return {
		page({ path, title }) {
			analyticsManager("event", "page_view", {
				page_location: origin + path,
				page_path: path,
				page_title: title,
			})
		},
		// The first param joins the event name so the events report reads on
		// its own; all params fold into category / label / value so the GA4
		// property needs only two custom dimensions for every event.
		track(action, data) {
			analyticsManager("event", gaEventName(action, data), foldToGAParams(data))
		},
		resetIdentity() {
			setUserId(undefined)
			analyticsManager("set", "user_properties", {
				tenant_name: undefined,
				app_version: appVersion,
			})
		},
		identify(userId, { tenantName }) {
			setUserId(userId)
			// `user_id` is the identity on the config call, not a property.
			analyticsManager("set", "user_properties", {
				tenant_name: tenantName,
				app_version: appVersion,
			})
		},
	}
}

export default createGAChannel
